import { useState, useEffect } from "react";
import { Cookie, Settings, X, Check } from "lucide-react";

// Banner de consentimiento de cookies — RGPD + Google Consent Mode v2.
// Estado por defecto (definido en index.html): todo denegado excepto necesarias.
// Al aceptar/rechazar, se actualiza el consent y se persiste en localStorage.

const STORAGE_KEY = "cookie-consent-v1";

const DEFAULT_STATE = {
  necessary: true, // siempre — no se puede desactivar
  analytics: false,
  marketing: false,
};

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function pushConsentToGtm(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Consent Mode v2 update
  window.dataLayer.push([
    "consent",
    "update",
    {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.analytics ? "granted" : "denied",
    },
  ]);
  // Evento custom para triggers en GTM
  window.dataLayer.push({
    event: "cookie_consent_update",
    consent_state: {
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing,
    },
  });
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(false);
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    const saved = loadSaved();
    if (!saved) {
      // Pequeño delay para no molestar en el primer paint
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
    setState(saved);
    // Escuchar evento global para re-abrir desde el footer
    const handler = () => {
      const s = loadSaved() || DEFAULT_STATE;
      setState(s);
      setDetail(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-banner", handler);
    return () => window.removeEventListener("open-cookie-banner", handler);
  }, []);

  const save = (next) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    pushConsentToGtm(next);
    setState(next);
    setVisible(false);
    setDetail(false);
  };

  const acceptAll = () =>
    save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () =>
    save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save(state);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      className="fixed inset-x-0 bottom-0 z-[60] p-4 md:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl bg-white border border-brand-line shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] overflow-hidden">
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-[#E6217A]/10 text-[#E6217A] flex items-center justify-center shrink-0">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-outfit font-semibold text-brand-primary text-base md:text-lg">
                Usamos cookies para mejorar tu experiencia
              </h2>
              <p className="mt-1.5 text-sm text-brand-ink/75 leading-relaxed">
                Usamos cookies propias y de terceros con fines analíticos y de
                marketing (Google, Meta) para entender cómo se usa la web y
                mostrarte contenido relevante. Puedes aceptar todas, rechazar
                las no esenciales o configurarlas.{" "}
                <a
                  href="/politica-de-cookies"
                  className="underline hover:text-brand-primary"
                >
                  Más información
                </a>
                .
              </p>
            </div>
            <button
              onClick={rejectAll}
              aria-label="Cerrar y rechazar cookies"
              className="text-brand-muted hover:text-brand-primary transition-colors md:hidden"
              data-testid="cookie-close-mobile"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {detail && (
            <div
              data-testid="cookie-preferences"
              className="mt-5 space-y-3 pl-0 md:pl-14"
            >
              <PreferenceRow
                label="Necesarias"
                description="Imprescindibles para el funcionamiento del sitio. No se pueden desactivar."
                checked={true}
                disabled
              />
              <PreferenceRow
                label="Analíticas"
                description="Nos ayudan a entender qué páginas ves y mejorar el sitio (Google Analytics)."
                checked={state.analytics}
                onChange={(v) => setState((s) => ({ ...s, analytics: v }))}
                testId="pref-analytics"
              />
              <PreferenceRow
                label="Marketing"
                description="Permiten personalizar anuncios y medir campañas (Google Ads, Meta Pixel)."
                checked={state.marketing}
                onChange={(v) => setState((s) => ({ ...s, marketing: v }))}
                testId="pref-marketing"
              />
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3 md:pl-14">
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-all"
              className="order-1 sm:order-3 flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#E6217A] hover:bg-[#C81B69] transition-colors text-white font-outfit font-semibold text-sm rounded-full py-3 px-6"
            >
              <Check className="h-4 w-4" />
              Aceptar todas
            </button>
            {detail ? (
              <button
                onClick={savePrefs}
                data-testid="cookie-save-prefs"
                className="order-2 flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover transition-colors text-white font-outfit font-semibold text-sm rounded-full py-3 px-6"
              >
                Guardar preferencias
              </button>
            ) : (
              <button
                onClick={() => setDetail(true)}
                data-testid="cookie-configure"
                className="order-2 flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-brand-line hover:border-brand-primary text-brand-primary font-outfit font-semibold text-sm rounded-full py-3 px-6"
              >
                <Settings className="h-4 w-4" />
                Configurar
              </button>
            )}
            <button
              onClick={rejectAll}
              data-testid="cookie-reject-all"
              className="order-3 sm:order-1 flex-1 sm:flex-none text-brand-muted hover:text-brand-primary font-outfit font-medium text-sm py-3 px-4 transition-colors"
            >
              Rechazar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreferenceRow({ label, description, checked, onChange, disabled, testId }) {
  return (
    <label
      className={`flex items-start gap-3 rounded-xl border border-brand-line p-3 ${
        disabled ? "bg-brand-surface/60" : "bg-white hover:border-brand-primary cursor-pointer"
      } transition-colors`}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-[#E6217A] disabled:opacity-50"
      />
      <div className="min-w-0">
        <p className="font-outfit font-semibold text-brand-primary text-sm">
          {label} {disabled && <span className="text-xs text-brand-muted font-normal">· Siempre activas</span>}
        </p>
        <p className="text-xs text-brand-ink/70 mt-0.5 leading-relaxed">
          {description}
        </p>
      </div>
    </label>
  );
}
